/**
 * Default testId used by the user profile card when the consumer does not pass one.
 * Centralised so the `getTestId` factory in `UserProfileCard` and
 * `UserProfileCardWithTrigger` agrees on what counts as "default".
 */
export const DEFAULT_USER_PROFILE_CARD_TEST_ID = 'user-profile-card';

/**
 * Builds the test id factory used to derive sub-element testIds from a single
 * user-scoped base. Steps:
 * 1. Use `user-profile-card-<userId>` as the base.
 * 2. When the consumer passes a non-default `testId`, suffix it with `--<testId>`
 *    so multiple cards on the same page stay disambiguated.
 * 3. Returns `undefined` when `userId` is empty (e.g. before profile data loads)
 *    to avoid unscoped IDs that could collide across multiple loading cards.
 */
export const makeGetTestId = (
	userId: string,
	testId: string | undefined,
): ((suffix: string) => string | undefined) => {
	const baseTestId = `user-profile-card-${userId}`;
	const isDefaultTestId = !testId || testId === DEFAULT_USER_PROFILE_CARD_TEST_ID;
	const suffixModifier = isDefaultTestId ? '' : `--${testId}`;
	return (suffix: string) => (userId ? `${baseTestId}-${suffix}${suffixModifier}` : undefined);
};
